// src/layouts/dashboard/StationDashboard.jsx
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

import Card from "@mui/material/Card";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Divider from "@mui/material/Divider";

import IngressEgressChart from "../../components/IngressEgressChart";
import MultiLineIngressEgressChart from "../../components/MultiLineIngressEgressChart";
import AdvancedControls from "../../components/AdvancedControls";
import ViewSummaryChips from "../../components/ViewSummaryChips";

import stationConfig from "../../config/stationConfig.json";
import { runFilterAndGroup } from "../../utils/runFilterAndGroup";
import { buildIngressEgressSeriesByGroup, alignGroupsToUnifiedTimeline } from "../../utils/flowTimeSeries";
import { computeOccupancyFromFlowRows } from "../../utils/occupancy";
import { searchToState, stateToSearch, normalizeViewState } from "../../utils/viewState";
import StationTreeTable from "../../components/StationTreeTable";
import OlympicParkMaskMap from "../../components/OlympicParkMaskMap";

// helpers
function formatInt(n) {
  const x = Number(n ?? 0);
  if (!Number.isFinite(x)) return "0";
  return Math.round(x).toLocaleString();
}

function StatCard({ label, value, fallback = "0" }) {
  return (
    <SoftBox>
      <SoftTypography variant="caption" sx={{ opacity: 0.7 }}>
        {label}
      </SoftTypography>
      <SoftTypography variant="h5" fontWeight="bold">
        {value != null ? Math.round(value).toLocaleString() : fallback}
      </SoftTypography>
    </SoftBox>
  );
}


export default function StationDashboard({
  flowRows = [],
  startLimit,
  endLimit,
  startTime,
  endTime,
  setStartTime,
  setEndTime,
}) {
  const { tree, ui } = stationConfig;

  const location = useLocation();
  const navigate = useNavigate();

  // URL <-> state
  const [view, setView] = useState(() => searchToState(location.search));

  const [epoch, setEpoch] = useState(0);

  useEffect(() => {
    setView(searchToState(location.search));
  }, [location.search]);

  useEffect(() => {
    const next = stateToSearch(view);
    if (next !== location.search) {
      navigate({ pathname: location.pathname, search: next }, { replace: true });
    }
  }, [view, location.pathname, location.search, navigate]);

  const updateView = (patch) =>
    setView((prev) =>
      normalizeViewState({
        ...prev,
        ...patch,
        tab: patch.tab ?? prev.tab // 🔒 never lose the current tab
      })
    );

  // Build uuid->group mapping (NOTE: no Area/Time-window controls in UI now)
  const { uuidToGroupKey } = useMemo(() => {
    // groupById doesn't matter for occupancy filtering; we pick the current grouping for flow tab,
    // and a sane default otherwise.
    const groupById = view.tab === "flow" ? view.grouping : "subgroup";

    return runFilterAndGroup({
      tree,
      ui,
      filterId: view.areaFilterId ?? "all", // kept for backwards compatibility with config/URL
      groupById
    });
  }, [tree, ui, view.tab, view.grouping, view.areaFilterId]);

  const allowedUuids = useMemo(() => new Set(uuidToGroupKey.keys()), [uuidToGroupKey]);

  // OCCUPANCY (primary)
  const occupancy = useMemo(() => {
    // Occupancy computed from raw rows (most reliable).
    return computeOccupancyFromFlowRows(flowRows, allowedUuids, {
      clamp0: true,
      zeroEnd: true
    });
  }, [flowRows, allowedUuids]);

  const currentOcc =
    occupancy.occupancy?.length ? occupancy.occupancy[occupancy.occupancy.length - 1] : 0;

  // FLOW ANALYSIS
  const flowSeriesByGroup = useMemo(() => {
    if (view.tab !== "flow") return {};
    return buildIngressEgressSeriesByGroup(flowRows, uuidToGroupKey, view.flowMode);
  }, [view.tab, flowRows, uuidToGroupKey, view.flowMode]);

  const flowUnified = useMemo(() => {
    if (view.tab !== "flow") return { labels: [], series: {} };
    return alignGroupsToUnifiedTimeline(flowSeriesByGroup);
  }, [view.tab, flowSeriesByGroup]);

  // Chart metric for MultiLine chart
  const flowMetric = view.metric; // "ingress" | "egress" | "net"

  const instantStats = useMemo(() => {
    if (epoch == null) return null;

    // find rows at this epoch
    const rowsAtEpoch = flowRows.filter(r => r.start === epoch);

    const ingress = rowsAtEpoch.reduce((s, r) => s + (r.in || 0), 0);
    const egress = rowsAtEpoch.reduce((s, r) => s + (r.out || 0), 0);

    // occupancy series assumed aligned by label/epoch
    let occ = null;
    if (occupancy?.labels && occupancy?.occupancy) {
      const idx = occupancy.labels.indexOf(epoch);
      if (idx !== -1) occ = occupancy.occupancy[idx];
    }

    return {
      ingress,
      egress,
      net: ingress - egress,
      occupancy: occ
    };
  }, [epoch, flowRows, occupancy]);

  return (
    <SoftBox display="grid" gap={2}>
      {/* TOP NAV */}
      <Card>
        <Tabs
          value={view.tab}
          onChange={(_, v) => updateView({ tab: v })}
          sx={{ minHeight: 42 }}
        >
          <Tab value="occupancy" label="Occupancy" sx={{ minHeight: 42 }} />
          <Tab value="flow" label="Flow" sx={{ minHeight: 42 }} />
        </Tabs>
      </Card>

      {/* SUMMARY CHIPS */}
      <Card>
        <SoftBox p={2}>
          <ViewSummaryChips view={view} />
        </SoftBox>
      </Card>
      <StationTreeTable
        tree={tree}
        statsByUuid={[]}
        epoch={epoch}
      />
      <Card>
        <SoftBox p={2.5} display="grid" gap={2}>
          <SoftTypography variant="overline" sx={{ opacity: 0.7 }}>
            TIME INSPECTOR
          </SoftTypography>

          {/* Epoch */}
          <SoftBox>
            <SoftTypography variant="caption" sx={{ opacity: 0.75 }}>
              Epoch
            </SoftTypography>
            <SoftTypography variant="h6" fontWeight="medium">
              {epoch != null ? epoch : "—"}
            </SoftTypography>
          </SoftBox>

          {/* Stats */}
          <SoftBox
            display="grid"
            gridTemplateColumns={{ xs: "1fr 1fr", md: "repeat(4, 1fr)" }}
            gap={2}
          >
            <StatCard
              label="Occupancy"
              value={instantStats?.occupancy}
              fallback="—"
            />
            <StatCard
              label="Ingress"
              value={instantStats?.ingress}
              fallback="—"
            />
            <StatCard
              label="Egress"
              value={instantStats?.egress}
              fallback="—"
            />
            <StatCard
              label="Net"
              value={instantStats?.net}
              fallback="—"
            />
          </SoftBox>
        </SoftBox>
      </Card>
      <OlympicParkMaskMap/>
      {/* OCCUPANCY TAB */}
      {view.tab === "occupancy" && (
        <>
          <Card>
            <SoftBox p={3}>
              <SoftTypography variant="overline" sx={{ opacity: 0.7 }}>
                CURRENT OCCUPANCY
              </SoftTypography>
              <SoftTypography variant="h3" fontWeight="bold">
                {formatInt(currentOcc)}
              </SoftTypography>
            </SoftBox>
          </Card>

          <Card>
            <SoftBox p={2} height={400}>
              <SoftTypography variant="h6">People currently in area</SoftTypography>
              <Divider sx={{ my: 1.5 }} />
              <IngressEgressChart
                labels={occupancy.labels}
                ingress={occupancy.occupancy}
                egress={new Array(occupancy.occupancy.length).fill(0)}
                chartType="inout"
                startLimit={startLimit}
                endLimit={endLimit}
                startTime={startTime}
                endTime={endTime}

                onHoverEpoch={setEpoch}
                onZoom={({ newStart, newEnd }) => {
                  setStartTime(newStart);
                  setEndTime(newEnd);
                }}
              />
            </SoftBox>
          </Card>
        </>
      )}

      {/* FLOW TAB */}
      {view.tab === "flow" && (
        <>
          <AdvancedControls view={view} onChange={updateView} />

          <Card>
            <SoftBox p={2}>
              <SoftTypography variant="h6">Flow analysis</SoftTypography>
              <Divider sx={{ my: 1.5 }} />

              <MultiLineIngressEgressChart
                labels={flowUnified.labels}
                series={flowUnified.series}
                metric={flowMetric}
                stacked={view.viewMode === "compare" ? view.stacked : false}
                showTotal={view.viewMode === "compare" ? view.showTotal : true}
                startLimit={startLimit}
                endLimit={endLimit}
                startTime={startTime}
                endTime={endTime}

                onHoverEpoch={setEpoch}
                onZoom={({ newStart, newEnd }) => {
                  setStartTime(newStart);
                  setEndTime(newEnd);
                }}
              />
            </SoftBox>
          </Card>
        </>
      )}
    </SoftBox>
  );
}
